import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  Radio,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface ProductOption {
  id: string;
  name: string;
  manufacturerName?: string;
}

interface Candidate {
  id: string;
  name: string;
  manufacturerName?: string;
  formulationName?: string;
  labelReferenceNumber?: string;
  activeIngredients: string[];
  categories: string[];
  recordLineCount: number;
  hasMsds: boolean;
  hasLabel: boolean;
  archivedDate?: string;
}

interface Preview {
  loser: Candidate;
  winner: Candidate;
  recordLineCount: number;
  recordCount: number;
  recordsWithBothCount: number;
  importedRowCount: number;
  legacyImportRowCount: number;
  fieldSeasonCount: number;
  aliasCount: number;
  documentCount: number;
  displayProductCount: number;
  filledFromLoser: string[];
  movesActiveIngredients: boolean;
  categoriesAdded: string[];
}

interface Props {
  // The product the merge was started from (a product list row).
  product: ProductOption | null;
  onClose: () => void;
  onMerged: () => void;
}

// Merges two products (ProductMergeController): the admin picks the other
// product and which one to keep. Everything that pointed at the other one
// (record lines, import rows, aliases, documents...) moves to the kept one,
// and the other is archived.
const ProductMergeDialog: React.FC<Props> = ({ product, onClose, onMerged }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [other, setOther] = useState<ProductOption | null>(null);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [search, setSearch] = useState('');
  const [keepId, setKeepId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setOther(null);
    setKeepId(null);
    setPreview(null);
    setSearch('');
    setError(null);
  }, [product?.id]);

  useEffect(() => {
    if (!product) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const params = new URLSearchParams({ pageSize: '20' });
      if (search) params.set('search', search);
      const response = await fetch(`/api/products?${params.toString()}`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setOptions(data.items.filter((p: ProductOption) => p.id !== product.id));
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, product, authHeaders]);

  const loserId = product && other ? (keepId === product.id ? other.id : product.id) : null;
  const winnerId = product && other ? (keepId === product.id ? product.id : other.id) : null;

  useEffect(() => {
    if (!loserId || !winnerId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setError(null);
    (async () => {
      try {
        const response = await fetch(`/api/admin/product-merge/preview?loserId=${loserId}&winnerId=${winnerId}`, { headers: authHeaders() });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to preview the merge');
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setError(err instanceof Error ? err.message : 'An error occurred');
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loserId, winnerId, authHeaders]);

  const handleMerge = async () => {
    if (!preview) return;
    if (!window.confirm(`Merge "${preview.loser.name}" into "${preview.winner.name}"? "${preview.loser.name}" will be archived. This can't be undone from the app.`)) return;
    setMerging(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/product-merge/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ loserProductId: preview.loser.id, winnerProductId: preview.winner.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Merge failed');
      onMerged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setMerging(false);
    }
  };

  const renderCandidate = (candidate: Candidate) => {
    const keep = candidate.id === winnerId;
    return (
      <Paper
        variant="outlined"
        onClick={() => setKeepId(candidate.id)}
        sx={{ p: 1.5, height: '100%', cursor: 'pointer', borderColor: keep ? 'success.main' : 'divider', borderWidth: keep ? 2 : 1 }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center">
            <Radio size="small" checked={keep} onChange={() => setKeepId(candidate.id)} sx={{ ml: -1 }} />
            <Typography variant="body2" fontWeight={600}>{keep ? 'Keep' : 'Merge away and archive'}</Typography>
          </Box>
          {candidate.archivedDate && <Chip label="Archived" size="small" />}
        </Box>
        <Typography variant="subtitle1" sx={{ mt: 0.5, wordBreak: 'break-word' }}>{candidate.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {[candidate.manufacturerName, candidate.formulationName].filter(Boolean).join(' · ') || 'No manufacturer or formulation'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {candidate.activeIngredients.length > 0 ? candidate.activeIngredients.join(', ') : 'No active ingredients'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {candidate.categories.length > 0 ? candidate.categories.join(', ') : 'No categories'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>{candidate.recordLineCount}</strong> record line(s)
          {candidate.labelReferenceNumber && ` · label ${candidate.labelReferenceNumber}`}
          {candidate.hasMsds && ' · MSDS'}
          {candidate.hasLabel && ' · Label PDF'}
        </Typography>
      </Paper>
    );
  };

  const moves = preview
    ? [
        preview.recordLineCount > 0 && `${preview.recordLineCount} record line(s) on ${preview.recordCount} record(s)`,
        preview.importedRowCount > 0 && `${preview.importedRowCount} import row(s)`,
        preview.legacyImportRowCount > 0 && `${preview.legacyImportRowCount} legacy CCA import row(s)`,
        preview.fieldSeasonCount > 0 && `${preview.fieldSeasonCount} field season variety link(s)`,
        preview.aliasCount > 0 && `${preview.aliasCount} import alias(es)`,
        preview.documentCount > 0 && `${preview.documentCount} document(s)`,
        preview.displayProductCount > 0 && `${preview.displayProductCount} product(s) that display as it`,
      ].filter(Boolean)
    : [];

  return (
    <Dialog open={!!product} onClose={merging ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Merge Products</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick the duplicate of <strong>{product?.name}</strong>, then which one to keep. Everything that points at the
          other one moves to the one you keep, and the other is archived.
        </Typography>
        <Autocomplete<ProductOption>
          options={options}
          value={other}
          filterOptions={(opts) => opts}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onInputChange={(_, value) => setSearch(value)}
          onChange={(_, value) => {
            setOther(value);
            setKeepId(value?.id ?? null);
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                {option.manufacturerName && (
                  <Typography variant="caption" color="text.secondary">{option.manufacturerName}</Typography>
                )}
              </Box>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Other product" placeholder="Search products..." />}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loadingPreview && !preview && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {preview && (
          <>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {[preview.winner, preview.loser]
                .sort((a, b) => (a.id === product?.id ? -1 : b.id === product?.id ? 1 : 0))
                .map((candidate) => (
                  <Grid item xs={12} sm={6} key={candidate.id}>
                    {renderCandidate(candidate)}
                  </Grid>
                ))}
            </Grid>

            <Box sx={{ mt: 2, opacity: loadingPreview ? 0.5 : 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Merging &quot;{preview.loser.name}&quot; into &quot;{preview.winner.name}&quot;
              </Typography>
              <Typography variant="body2" component="div">
                {moves.length > 0 ? (
                  <>
                    Moves:
                    <ul style={{ margin: '4px 0' }}>
                      {moves.map((m) => <li key={m as string}>{m}</li>)}
                    </ul>
                  </>
                ) : (
                  'Nothing points at it, so it is just archived.'
                )}
              </Typography>
              {preview.filledFromLoser.length > 0 && (
                <Typography variant="body2">
                  Fills blank fields on the kept product: {preview.filledFromLoser.join(', ')}.
                </Typography>
              )}
              {preview.movesActiveIngredients && (
                <Typography variant="body2">The kept product has no active ingredients, so it takes these.</Typography>
              )}
              {preview.categoriesAdded.length > 0 && (
                <Typography variant="body2">Adds categories: {preview.categoriesAdded.join(', ')}.</Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Later imports of &quot;{preview.loser.name}&quot; will match the kept product.
              </Typography>
              {preview.recordsWithBothCount > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {preview.recordsWithBothCount} record(s) have a line of each product. They&apos;ll show two lines of
                  &quot;{preview.winner.name}&quot;, which are left as they are.
                </Alert>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={merging}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={!preview || loadingPreview || merging} onClick={handleMerge}>
          {merging ? 'Merging...' : preview ? `Merge into "${preview.winner.name}"` : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductMergeDialog;
